import 'react-app-polyfill/ie9';
import 'react-app-polyfill/stable';
import React from 'react';
import {createRoot, hydrateRoot} from 'react-dom/client';
import 'lazysizes';
import * as Sentry from '@sentry/browser';
import 'normalize.css/normalize.css';
import 'react-dates/lib/css/_datepicker.css';
import {getApiConfig} from './config/configProvider';

import registerServiceWorker from './registerServiceWorker';
import Root from './client/Root/Root.container';
import configureStore from './store/config.store';
import {GoogleAnalyticsHelper} from './helpers/googleAnalytics.helper';
import {setServerConfig} from './config/configProvider';
import {
  ERRORS,
  HTTP_STATUS,
  SENTRY_ALLOWED_URLS,
  SENTRY_APPLICATION_KEY,
  URL,
} from './constants/constants';
import {shouldDropDuplicateSentryEvent} from './helpers/sentry.helper';

const init = preloadedState => {
  const store = configureStore(preloadedState.store);
  if (preloadedState) {
    setServerConfig(preloadedState.CONFIG_JS, false, preloadedState.release);
  }

  GoogleAnalyticsHelper.initialize();
  const config = getApiConfig();
  const isDev = window.location.host.indexOf(URL.JJS_PROD) === -1;
  Sentry.init({
    dsn: config && config.sentryUrl,
    ignoreErrors: [
      ERRORS.WEB_SOCKET,
      ERRORS.TIMEOUT,
      ERRORS.NOT_AUTHORIZED,
      ERRORS.NON_ERROR_PROMISE,
      ERRORS.REQUEST_ABORTED,
      ERRORS.REQUEST_ABORTED_RX,
      ERRORS.NEXTWORK_ERROR,
      ERRORS.NEXTWORK_ERROR_RX,
      ERRORS.MODULE_IMPORT_FAILED,
      ERRORS.SAFARI_LOAD_FAILED,
      ERRORS.RUNTIME_SEND_MESSAGE,
      ERRORS.SAFARI_CUSTOM_EVENT_REJECTION,
    ],
    environment: isDev ? 'development' : 'production',
    integrations: [
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: [SENTRY_APPLICATION_KEY],
        behaviour: 'drop-error-if-contains-third-party-frames',
      }),
    ],
    allowUrls: [SENTRY_ALLOWED_URLS],
    beforeSend(event, hint) {
      const error = hint?.originalException;
      if (error) {
        const str = JSON.stringify(error);
        if (
          str.includes('"status":401') ||
          str.includes('"status": 401') ||
          str.includes('401: Unauthorized') ||
          (error.status && error.status === 401)
        ) {
          return null;
        }
        if (
          error?.response?.status === HTTP_STATUS.BAD_GATEWAY &&
          error?.config?.url?.includes(URL.GRAPHQL_PATH)
        ) {
          return null;
        }
      }

      const errorValue = event.exception?.values?.[0]?.value;
      if (errorValue && /^[A-Z][a-z]?$/.test(errorValue)) {
        return null;
      }

      if (shouldDropDuplicateSentryEvent(event)) {
        return null;
      }

      return event;
    },
  });
  const container = document.getElementById('jjfoods');
  if (!!module.hot) {
    const root = createRoot(container);
    root.render(<Root store={store} />);
  } else {
    hydrateRoot(container, <Root store={store} />, {
      onUncaughtError: error => {
        // TODO console.log('Hydrate onUncaughtError', error);
      },
      onCaughtError: error => {
        // TODO console.log('Hydrate onCaughtError', error)
      },
      onRecoverableError: error => {
        // TODO console.log('Hydrate onRecoverableError', error),
      },
    });
  }

  registerServiceWorker();
};
if (!window.__PRELOADED_STATE__ && !module.hot) {
  document.addEventListener('DOMContentLoaded', () => {
    let observer;
    let gotData = false;
    observer = new MutationObserver((mutations, obs) => {
      for (let mutation of mutations) {
        if (mutation.type === 'childList' && !gotData) {
          const configElement = document.getElementById('jjs-script');
          if (configElement && configElement.textContent) {
            gotData = true;
            let preloadedState;
            try {
              preloadedState = JSON.parse(configElement.textContent);
            } catch (e) {
              Sentry && Sentry.captureException && Sentry.captureException(e);
            }
            obs.disconnect();
            init(preloadedState);
            return;
          }
        }
      }
    });
    observer.observe(document.body, {childList: true, subtree: true});
  });
} else {
  const preloadedState = window.__PRELOADED_STATE__ || {};
  window.__PRELOADED_STATE__ = null;
  if (preloadedState.store && preloadedState.store.prerender === true) {
    window.prerenderReady = true;
  }
  init(preloadedState);
}
